type WindCompassOverlayProps = {
  rawRelativeWindDeg: number;
  adjustedRelativeWindDeg: number;
  windBucket: string;
};

function WindCompassOverlay({
  rawRelativeWindDeg,
  adjustedRelativeWindDeg,
  windBucket,
}: WindCompassOverlayProps) {
  const center = 90;
  const radius = 58;
  const shotLen = 40;
  const rawWindLen = 44;
  const finalWindLen = 50;

  // Blue shot arrow: always straight up in golfer / shot frame
  const shotTipX = center;
  const shotTipY = center - shotLen;

  // In shot-aligned display:
  // 0 = helping (up), +90 = from right, -90 = from left, ±180 = hurting (down)
  const rawRad = (rawRelativeWindDeg * Math.PI) / 180;
  const finalRad = (adjustedRelativeWindDeg * Math.PI) / 180;

  const rawTipX = center + Math.sin(rawRad) * rawWindLen;
  const rawTipY = center - Math.cos(rawRad) * rawWindLen;

  const finalTipX = center + Math.sin(finalRad) * finalWindLen;
  const finalTipY = center - Math.cos(finalRad) * finalWindLen;

  return (
    <div className="border border-slate-600 rounded-xl bg-slate-800/70 p-4 mt-3">
      <h3 className="text-sm font-semibold text-slate-100 mb-3">
        Wind Compass
      </h3>

      <svg width="180" height="180" viewBox="0 0 180 180">
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="rgb(148 163 184)"
          strokeWidth="2"
        />

        {/* Optional frame labels */}
        <text x={center} y={22} textAnchor="middle" fontSize="12" fill="rgb(203 213 225)">
          Helping
        </text>
        <text x={center} y={172} textAnchor="middle" fontSize="12" fill="rgb(203 213 225)">
          Hurting
        </text>
        <text x={18} y={94} textAnchor="start" fontSize="12" fill="rgb(203 213 225)">
          Left
        </text>
        <text x={162} y={94} textAnchor="end" fontSize="12" fill="rgb(203 213 225)">
          Right
        </text>

        {/* Blue shot arrow: direction of play */}
        <line
          x1={center}
          y1={center}
          x2={shotTipX}
          y2={shotTipY}
          stroke="#60A5FA"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <polygon
          points={`${shotTipX},${shotTipY - 8} ${shotTipX - 6},${shotTipY + 6} ${shotTipX + 6},${shotTipY + 6}`}
          fill="#60A5FA"
        />

        {/* Orange raw wind relative to shot */}
        <line
          x1={center}
          y1={center}
          x2={rawTipX}
          y2={rawTipY}
          stroke="#F59E0B"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <polygon
          points={(() => {
            const headLen = 9;
            const spread = Math.PI / 7;
            const leftX = rawTipX - headLen * Math.sin(rawRad - spread);
            const leftY = rawTipY + headLen * Math.cos(rawRad - spread);
            const rightX = rawTipX - headLen * Math.sin(rawRad + spread);
            const rightY = rawTipY + headLen * Math.cos(rawRad + spread);

            return `${rawTipX},${rawTipY} ${leftX},${leftY} ${rightX},${rightY}`;
          })()}
          fill="#F59E0B"
        />

        {/* Green adjusted/final wind relative to shot */}
        <line
          x1={center}
          y1={center}
          x2={finalTipX}
          y2={finalTipY}
          stroke="#4ADE80"
          strokeWidth="4"
          strokeLinecap="round"
        />
        <polygon
          points={(() => {
            const headLen = 9;
            const spread = Math.PI / 7;
            const leftX = finalTipX - headLen * Math.sin(finalRad - spread);
            const leftY = finalTipY + headLen * Math.cos(finalRad - spread);
            const rightX = finalTipX - headLen * Math.sin(finalRad + spread);
            const rightY = finalTipY + headLen * Math.cos(finalRad + spread);

            return `${finalTipX},${finalTipY} ${leftX},${leftY} ${rightX},${rightY}`;
          })()}
          fill="#4ADE80"
        />

        {/* Center dot */}
        <circle cx={center} cy={center} r={3} fill="rgb(226 232 240)" />
      </svg>

      <div className="text-sm mt-3 space-y-2">
        <div className="flex items-center gap-2 text-slate-100">
          <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: "#60A5FA" }} />
          <span>Shot direction</span>
        </div>

        <div className="flex items-center gap-2 text-slate-100">
          <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: "#F59E0B" }} />
          <span>
            Raw wind: {rawRelativeWindDeg.toFixed(1)}°
          </span>
        </div>

        <div className="flex items-center gap-2 text-slate-100">
          <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: "#4ADE80" }} />
          <span>
            Final wind: {windBucket.replace(/-/g, " ")} ({adjustedRelativeWindDeg.toFixed(1)}°)
          </span>
        </div>
      </div>
    </div>
  );
}

export default WindCompassOverlay;